import { deserialize_ApiErrorResponse } from "@newgenerated/system/serializerDeserializer/ApiErrorResponse";
import { assert, assertIsDefined } from "@utils/assertion";
import { Env } from "@utils/env";
import { parseJsonWithNumbersAsBigint } from "@utils/json";
import { handleLockerResponseHeaders } from "@utils/type/locker";

export type ApiResultError = { kind: "error"; errorCode: string | null; errorMessage: string | null };
export type ApiResultSuccess<T = undefined> = [T] extends [undefined] ? { kind: "success" } : { kind: "success"; data: T };

export type ApiResultWithData<T> = { kind: "success"; data: T } | ApiResultError;
export type ApiResult<T = undefined> = ApiResultSuccess<T> | ApiResultError;

export type GaApiCallProps = {
  path: string;
  method: "GET" | "PUT" | "POST" | "DELETE";
  requestBody: string | null;
};

export type FakeResponse = {
  path: RegExp;
  status: number;
  data: string;
};

function initialUnitTestState(): { kind: "UNITTEST"; responses: FakeResponse[] } {
  return {
    kind: "UNITTEST",
    responses: [
      {
        path: /\/session-keepalive/,
        status: 200,
        data: '"faketoken"',
      },
    ],
  };
}

let gaFetchInTest: { kind: "PRODUCTION" } | { kind: "UNITTEST"; responses: FakeResponse[] } | { kind: "TOMCATTEST"; cookies: Map<string, string>; authorizationHeader: string | null } = (() => {
  if (typeof process !== "object") {
    //runs in browser
    return { kind: "PRODUCTION" };
  }
  if (typeof process.env.JEST_WORKER_ID === "string") {
    //unit tests must never do real network calls (jsdom has no fetch anyway)
    return initialUnitTestState();
  }
  return {
    kind: "TOMCATTEST",
    cookies: new Map<string, string>(),
    authorizationHeader: null,
  };
})();

let gaAuthorizationBearerToken: string | null = null;

export function setGaAuthorizationBearerToken(token: string): void {
  gaAuthorizationBearerToken = token;
}

export function gaSetupFakeResponse(response: FakeResponse): void {
  if (gaFetchInTest.kind !== "UNITTEST") {
    gaFetchInTest = initialUnitTestState();
  }

  gaFetchInTest.responses.push(response);
}

/**
 * gaInternalFetch exists to make fetch() work in vitest environment.
 * The vitest environment does not handle cookies and Set-Cookies from the server correctly, hence we implement it here manually
 *
 * For the browser case, this method does simply forward the to fetch()
 */
export async function gaInternalFetch(path: string, options: { overrideBaseUrl?: string; headers?: Record<string, string>; method: "GET" | "PUT" | "POST" | "DELETE"; body?: string }): Promise<Response> {
  assert(path.startsWith("/"));

  const baseUrl = (() => {
    if (options.overrideBaseUrl !== undefined) {
      return options.overrideBaseUrl;
    }
    if (typeof window !== "undefined") {
      return window.location.origin;
    }
    return Env.PUBLIC_BACKEND_URL;
  })();

  const url = new URL(path, baseUrl);

  const response = await (async (): Promise<Response> => {
    switch (gaFetchInTest.kind) {
      case "PRODUCTION":
        //standard browser case: nothing special
        return fetch(url, options);
      case "TOMCATTEST": {
        //vitest case: fix cookies (hopefully no longer necessary in the future)
        let cookieString = typeof document !== "undefined" ? document.cookie : "";
        if (cookieString.length > 0) {
          cookieString += ";";
        }
        for (const [key, value] of gaFetchInTest.cookies.entries()) {
          cookieString += `${key}=${value};`;
        }
        const response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            Cookie: cookieString,
            ...(gaAuthorizationBearerToken != null
              ? {
                  Authorization: `Bearer ${gaAuthorizationBearerToken}`,
                }
              : {}),
          },
        });
        const setCookies = response.headers.getSetCookie();
        for (const cookie of setCookies) {
          const [key, value] = cookie.split("=", 2);
          assertIsDefined(key);
          assertIsDefined(value);
          const valueWithoutConfig = value.split(";")[0];
          assertIsDefined(valueWithoutConfig);
          gaFetchInTest.cookies.set(key, valueWithoutConfig);
        }
        return response;
      }
      case "UNITTEST": {
        for (const response of gaFetchInTest.responses) {
          if (response.path.test(path)) {
            return {
              arrayBuffer(): Promise<ArrayBuffer> {
                assert(false);
              },
              blob(): Promise<Blob> {
                assert(false);
              },
              body: null,
              bodyUsed: false,
              bytes(): Promise<Uint8Array<ArrayBuffer>> {
                assert(false);
              },
              clone(): Response {
                assert(false);
              },
              formData(): Promise<FormData> {
                assert(false);
              },
              headers: new Headers(),
              json(): Promise<unknown> {
                return Promise.resolve(parseJsonWithNumbersAsBigint(response.data));
              },
              ok: response.status === 200,
              redirected: false,
              status: response.status,
              statusText: "",
              text(): Promise<string> {
                return Promise.resolve(response.data);
              },
              type: "basic",
              url: "not implemented",
            };
          }
        }

        return {
          arrayBuffer(): Promise<ArrayBuffer> {
            assert(false);
          },
          blob(): Promise<Blob> {
            assert(false);
          },
          body: null,
          bodyUsed: false,
          bytes(): Promise<Uint8Array<ArrayBuffer>> {
            assert(false);
          },
          clone(): Response {
            assert(false);
          },
          formData(): Promise<FormData> {
            assert(false);
          },
          headers: new Headers(),
          json(): Promise<unknown> {
            return Promise.resolve({});
          },
          ok: false,
          redirected: false,
          status: 0,
          statusText: "",
          text(): Promise<string> {
            assert(false);
          },
          type: "basic",
          url: "",
        };
      }
    }
  })();

  handleLockerResponseHeaders((name) => response.headers.get(name));
  return response;
}

export async function gaApiCallWithoutAuth(props: { apiCallProps: GaApiCallProps; overrideBaseUrl?: string; authHeaders: Record<string, string> }): Promise<ApiResult<string>> {
  try {
    const response = await gaInternalFetch(props.apiCallProps.path, {
      overrideBaseUrl: props.overrideBaseUrl,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...props.authHeaders,
      },
      method: props.apiCallProps.method,
      body: props.apiCallProps.requestBody ?? undefined,
    });
    const text = await response.text();
    //empty response is valid and good
    if (!response.ok) {
      const errorResponse = deserialize_ApiErrorResponse(text);
      const message = `${props.apiCallProps.path} failed: status=${response.status} body=${text.length <= 50 ? text : text.substring(0, 50) + "..."}`;
      console.warn(message);

      const errorCode = errorResponse.success ? errorResponse.value.errorCode : null;
      const errorMessage = errorResponse.success ? errorResponse.value.errorMessage : null;
      return { kind: "error", errorCode, errorMessage };
    }
    return { kind: "success", data: text };
  } catch (error: unknown) {
    const message = error instanceof Error ? `${error.name}: ${error.message}` : `unknown error: ${typeof error}`;
    console.warn(`${props.apiCallProps.path} failed: ${message}`);
    return { kind: "error", errorCode: null, errorMessage: `Network error calling ${props.apiCallProps.path}: ${message}` };
  }
}
